import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { CertificationProviderState } from 'app/library/certification-provider/models/certification-provider.state';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddCertificateRecord,
    AddCertificateRecordFailure,
    AddCertificateRecords,
    AddCertificateRecordSuccess,
    AssignCertificateToModuleUser,
    AssignCertificateToModuleUserFailure,
    AssignCertificateToModuleUsers,
    AssignCertificateToModuleUsersFailure,
    AssignCertificateToModuleUsersSuccess,
    AssignCertificateToModuleUserSuccess,
    CreateCertificateRecord,
    CreateCertificateRecordFailure,
    CreateCertificateRecordSuccess,
    DeleteCertificateRecord,
    DeleteCertificateRecordFailure,
    DeleteCertificateRecordSuccess,
    FetchCertificateRecord,
    FetchCertificateRecordFailure,
    FetchCertificateRecords,
    FetchCertificateRecordsFailure,
    FetchCertificateRecordsSuccess,
    FetchCertificateRecordSuccess,
    SetCertificateRecord,
    SetCertificateRecords,
    UnsetCertificateRecord,
    UpdateCertificateRecord,
    UpdateCertificateRecordFailure,
    UpdateCertificateRecordSuccess,
} from '../actions';
import { CertificateRecordApiService } from '../services/certificate-record.api-service';
import { CertificateRecord } from './certificate-record';
import { CertificateRecordStateModel } from './certificate-record.state-model';

@State<CertificateRecordStateModel>({
    name: 'certificateRecord',
    defaults: new CertificateRecordStateModel(),
})
@Injectable()
export class CertificateRecordState extends EntityState<CertificateRecord> {
    @Selector()
    static getEntity(state: CertificateRecordStateModel, id: string = state.item): CertificateRecord {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CertificateRecordStateModel): CertificateRecord[] {
        if (!state.map) {
            return null;
        }

        return Array.from(state.map.values());
    }

    constructor(private certificateApiService: CertificateRecordApiService, private store: Store) {
        super(null, 0);
    }

    @Action(AddCertificateRecords)
    addEntities(ctx: StateContext<CertificateRecordStateModel>, action: AddCertificateRecords): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCertificateRecord)
    setEntity(ctx: StateContext<CertificateRecordStateModel>, action: SetCertificateRecord): void {
        this._setEntity(ctx, action, AddCertificateRecords);
    }

    @Action(UnsetCertificateRecord)
    unsetEntity(ctx: StateContext<CertificateRecordStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateCertificateRecord)
    createEntity(ctx: StateContext<CertificateRecordStateModel>, action: CreateCertificateRecord): void {
        this._createEntity(ctx, action, CreateCertificateRecordSuccess, CreateCertificateRecordFailure, AddCertificateRecords).then(() => {});
    }

    @Action(UpdateCertificateRecord)
    updateCertificateRecord({ dispatch }: StateContext<CertificateRecordStateModel>, action: UpdateCertificateRecord): void {
        this.certificateApiService
            .update(
                action.payload.certificateRecordId,
                action.payload.certificationId,
                action.payload.issueDate,
                action.payload.expirationDate,
                action.payload.notifyAboutExpiration
            )
            .subscribe(
                (entity) => {
                    dispatch([new AddCertificateRecords([entity]), new UpdateCertificateRecordSuccess(entity)]);
                },
                (response: HttpErrorResponse) => {
                    dispatch(new UpdateCertificateRecordFailure({ entityId: action.payload.certificateRecordId, response }));
                }
            );
    }

    @Action(DeleteCertificateRecord)
    deleteEntity(ctx: StateContext<CertificateRecordStateModel>, action: DeleteCertificateRecord): void {
        this._deleteEntity(ctx, action, DeleteCertificateRecordSuccess, DeleteCertificateRecordFailure).then(() => {});
    }

    @Action(FetchCertificateRecord)
    fetchEntity({ dispatch }: StateContext<CertificateRecordStateModel>, { payload }: FetchCertificateRecord): void {
        this.certificateApiService.getSingleCertificate(payload).subscribe(
            (certificate) => {
                dispatch([new SetCertificateRecord(certificate), new FetchCertificateRecordSuccess(certificate)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchCertificateRecordFailure({ entityId: payload, response }));
            }
        );
    }

    @Action(FetchCertificateRecordSuccess)
    fetchEntitySuccess(ctx: StateContext<CertificateRecordStateModel>, action: FetchCertificateRecordSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCertificateRecordFailure)
    fetchEntityFailure(ctx: StateContext<CertificateRecordStateModel>, action: FetchCertificateRecordFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetCertificateRecords)
    setEntities(ctx: StateContext<CertificateRecordStateModel>, action: SetCertificateRecords): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchCertificateRecords)
    fetchEntities(ctx: StateContext<CertificateRecordStateModel>): void {
        throw new Error('Not implemented');
    }

    @Action(FetchCertificateRecordsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<CertificateRecordStateModel>): void {
        throw new Error('Not implemented');
    }

    @Action(FetchCertificateRecordsFailure)
    fetchEntitiesFailure(ctx: StateContext<CertificateRecordStateModel>): void {
        throw new Error('Not implemented');
    }

    @Action(AddCertificateRecord)
    addCertificateRecord(ctx: StateContext<CertificationProviderState>, action: AddCertificateRecord): void {
        this.certificateApiService
            .addCertificate(
                action.payload.certificationId,
                action.payload.issueDate,
                action.payload.expirationDate,
                action.payload.sendNotification,
                action.payload.userId,
                action.payload.file,
                action.payload.notifyAboutExpiration
            )
            .subscribe(
                (entities) => {
                    ctx.dispatch(new AddCertificateRecordSuccess());
                },
                (response: HttpErrorResponse) => {
                    ctx.dispatch(new AddCertificateRecordFailure());
                }
            );
    }

    @Action(AssignCertificateToModuleUser)
    assignCertificateToModuleUser(ctx: StateContext<CertificationProviderState>, action: AssignCertificateToModuleUser): void {
        this.certificateApiService
            .assignCertificateToModuleUser(
                action.payload.participantId,
                action.payload.certificationId,
                action.payload.issueDate,
                action.payload.expirationDate,
                action.payload.sendNotification,
                action.payload.file
            )
            .subscribe(
                (entities) => {
                    ctx.dispatch(new AssignCertificateToModuleUserSuccess(entities));
                },
                (response: HttpErrorResponse) => {
                    ctx.dispatch(new AssignCertificateToModuleUserFailure());
                }
            );
    }

    @Action(AssignCertificateToModuleUsers)
    assignCertificateToModuleUsers(ctx: StateContext<CertificationProviderState>, action: AssignCertificateToModuleUsers): void {
        this.certificateApiService.assignCertificateToModuleUsers(action.payload.moduleUserIds, action.payload.moduleCertificationIds, action.payload.sendNotification).subscribe(
            (entities) => {
                ctx.dispatch(new AssignCertificateToModuleUsersSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new AssignCertificateToModuleUsersFailure());
            }
        );
    }
}
